import BoxWrapper from "../../../components/BoxWrapper";
import MoneyReceiveIcon from "../../../components/icons/MoneyReceiveIcon";
import MoneyUpIcon from "../../../components/icons/MoneyUpIcon";
import PercentageIcon from "../../../components/icons/PercentageIcon";
import TicketIcon from "../../../components/icons/TicketIcon";
import BagIcon from "../../../components/icons/BagIcon";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useMutation, useQuery, useQueryClient } from "react-query";
import apiService from "../../../service/apiService";
import { useParams } from "react-router-dom";
import { useCookie } from "../../../hooks/useCookie";
import { formatCurrency } from "../../../helpers/general";
import { useAuth } from "../../../hooks/useAuth";
import { useState } from "react";
import PromptModal from "../../../components/modals/PromptModal";
import { useToast } from "../../../hooks/useToast";
import { general } from "../../../locales/general";
import { apiConfig } from "../../../config/apiConfig";

interface ServiceSale {
  serviceLable: string;
  totalSales: number;
  totalTickets: number;
  totalNumberOfPersons: number;
  salesPercentage: number;
  ticketsPercentage: number;
}

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const EventStatistics = () => {
  const params = useParams();
  const { token } = useCookie("vToken");
  const { isAdmin } = useAuth();
  const { toastError, toastSuccess } = useToast();
  const queryClient = useQueryClient();
  const [payModalOpen, setPayModalOpen] = useState(false);

  const { data: statistics } = useQuery(["getEventStatistics"], () => {
    return apiService.MakeGetRequest(
      `events/${params?.eventId}/statistic`,
      token,
    );
  });

  const { data: event } = useQuery<Events>(["getEvent"], () => {
    return apiService.MakeGetRequest(`events/${params?.eventId}`);
  });

  const markBookingsPaid = useMutation(() => {
    return apiService.MakePutRequest(
      apiConfig.events.markBookingsPaid(params?.eventId as string),
      {},
      token,
    );
  });

  const createGradient = (color: string) => {
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) return color;
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, color);
    gradient.addColorStop(1, lightenColor(color, 50));
    return gradient;
  };

  const lightenColor = (color: string, amount: number) => {
    return (
      "#" +
      color
        .replace(/^#/, "")
        .replace(/../g, (color) =>
          (
            "0" +
            Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(
              16,
            )
          ).substr(-2),
        )
    );
  };

  return (
    <section>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="fs-4 fw-bold mb-0">Statistiques</h5>
        {isAdmin && (
          <button
            className="outline-none border-0 btn btn-primary rounded-pill text-white d-flex align-items-center gap-2 shadow-secondary"
            onClick={() => setPayModalOpen(true)}
            disabled={!params?.eventId}
          >
            Payer
          </button>
        )}
      </div>
      <BoxWrapper>
        <div className="container my-3 py-4">
          <h2 className="fw-medium mb-3 text-center">{event?.name}</h2>
          <div className="row text-center mb-4">
            <div className="col-md-12">
              <div className="shadow p-3 mb-4 rounded-5 d-flex justify-content-center align-items-center gap-3 border border-success ">
                <div className="rounded-circle bg-success p-3 bg-opacity-10">
                  <MoneyReceiveIcon
                    width="80"
                    height="80"
                    className="text-success text-center"
                  />
                </div>
                <div>
                  <h5>Total des ventes</h5>
                  <h4 className="fw-bold">
                    {formatCurrency(statistics?.totalSales ?? 0)} MAD
                  </h4>
                </div>
              </div>
            </div>
          </div>
          <div className="row text-center">
            <div className="col-6 col-md-3 px-2">
              <div className="shadow-sm mb-2 py-2 bg-primary text-white rounded-4 d-flex flex-column flex-md-row  align-items-center gap-2 gap-lg-4">
                <div className="rounded-circle bg-white ms-2 p-2">
                  <BagIcon className="w-5 h-5 text-primary" />
                </div>
                <div className="text-center text-md-start py-2 px-1 d-flex flex-column ">
                  <span className="text-sm text-md-lg">Nombre de ventes</span>
                  <span className="fw-semibold fs-6">
                    {statistics?.totalTickets ?? 0}
                  </span>
                </div>
              </div>
            </div>

            <div className="col-6 col-md-3 px-2">
              <div className="shadow-sm mb-2 py-2 bg-danger text-white rounded-4 d-flex flex-column flex-md-row  align-items-center gap-2 gap-lg-4">
                <div className="rounded-circle bg-white ms-2 p-2">
                  <TicketIcon className="w-5 h-5 text-danger" />
                </div>
                <div className="text-center text-md-start py-2 px-1 d-flex flex-column ">
                  <span className="text-sm text-md-lg">Nombre de tickets</span>
                  <span className="fw-semibold fs-6">
                    {statistics?.totalNumberOfPersons ?? 0}
                  </span>
                </div>
              </div>
            </div>

            <div className="col-6 col-md-3 px-2">
              <div className="shadow-sm mb-2 py-2 bg-purple text-white rounded-4 d-flex flex-column flex-md-row  align-items-center gap-2 gap-lg-4">
                <div className="rounded-circle bg-white ms-2 p-2">
                  <PercentageIcon className="w-5 h-5 text-purple " />
                </div>
                <div className="text-center text-md-start py-2 px-1 d-flex flex-column ">
                  <span className="text-sm text-md-lg">Commission</span>
                  <span className="fw-semibold fs-6">
                    {formatCurrency(statistics?.commission ?? 0)} MAD
                  </span>
                </div>
              </div>
            </div>

            <div className="col-6 col-md-3 px-2">
              <div className="shadow-sm mb-2 py-2 bg-black text-white rounded-4 d-flex flex-column flex-md-row  align-items-center gap-2 gap-lg-4">
                <div className="rounded-circle bg-white ms-2 p-2">
                  <MoneyUpIcon className="w-5 h-5 text-black" />
                </div>
                <div className="text-center text-md-start py-2 px-1 d-flex flex-column ">
                  <span className="text-sm text-md-lg">Nombre des gains</span>
                  <span className="fw-semibold fs-6">
                    {formatCurrency(statistics?.totalOfGains ?? 0)} MAD
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="row text-center mt-5 d-flex justify-content-center">
            <div className="col-md-6 col-lg-5 col-11">
              <h5>Pourcentage de vente de chaque service</h5>
              <div className="card border-0">
                <div className="card-body">
                  {statistics?.totalSales &&
                  statistics?.servicesSales.length > 0 ? (
                    <Pie
                      className="w-100 h-100"
                      data={{
                        labels: statistics?.servicesSales
                          .filter(
                            (s: ServiceSale) =>
                              s.salesPercentage && s.totalSales,
                          )
                          .map((s: ServiceSale) => s.serviceLable),
                        datasets: [
                          {
                            data: statistics?.servicesSales
                              .filter(
                                (s: ServiceSale) =>
                                  s.salesPercentage && s.totalSales,
                              )
                              .map((s: ServiceSale) => s.salesPercentage),
                            borderColor: ["transparent"],
                            backgroundColor: [
                              createGradient("#FF7722"),
                              createGradient("#22AAFF"),
                              createGradient("#000000"),
                              createGradient("#5F50FD"),
                              createGradient("#00AC4F"),

                              createGradient("#A0522D"),
                              createGradient("#00C2D1"),
                              createGradient("#FFCC00"),
                              createGradient("#888888"),
                              createGradient("#B22222"),
                              createGradient("#009999"),
                            ],
                          },
                        ],
                      }}
                      options={{
                        plugins: {
                          datalabels: {
                            display: true,
                            color: "white",
                            font: {
                              family: "Poppins",
                              size: 14,
                            },
                            labels: {
                              name: {
                                align: "center",
                                textAlign: "center",
                                font: { size: 20, weight: "bold" },
                                formatter: (value: number, context: any) => {
                                  const percentage =
                                    statistics?.servicesSales[context.dataIndex]
                                      .salesPercentage;
                                  return `${percentage ?? 0} %`;
                                },
                              },
                              value: {
                                align: "bottom",
                                textAlign: "center",
                                formatter: (value: number, context: any) => {
                                  const nbTickets =
                                    statistics?.servicesSales[context.dataIndex]
                                      .totalNumberOfPersons;
                                  return `${nbTickets ?? 0} ${
                                    nbTickets > 1 ? "tickets" : "ticket"
                                  }`;
                                },
                                padding: 6,
                              },
                            },
                          },
                          legend: {
                            labels: {
                              color: "#000000",
                              usePointStyle: true,
                              pointStyle: "circle",
                              font: { family: "Poppins", size: 14 },
                            },
                            position: "bottom",
                          },
                          tooltip: {
                            enabled: true,
                            callbacks: {
                              label: (context: any) => {
                                const sales =
                                  statistics?.servicesSales[context.dataIndex]
                                    .totalSales;
                                return `${sales ?? 0} MAD`;
                              },
                            },
                          },
                        },
                        responsive: true,
                      }}
                    />
                  ) : (
                    <>
                      <p className="mt-2 text-muted">
                        Il n'y a pas de données à afficher
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </BoxWrapper>
      <PromptModal
        open={payModalOpen}
        message="Voulez-vous marquer tous les paiements in-app de cet événement comme payés au lieu ?"
        loading={markBookingsPaid.isLoading}
        onClose={() => setPayModalOpen(false)}
        onAccept={() => {
          markBookingsPaid.mutate(undefined, {
            onSuccess(data: { updatedCount?: number }) {
              toastSuccess(
                data?.updatedCount != null
                  ? `${data.updatedCount} réservation(s) marquée(s) comme payée(s)`
                  : "Opération réussie",
              );
              setPayModalOpen(false);
              queryClient.invalidateQueries("getEventStatistics");
              queryClient.invalidateQueries("placePendingAmount");
            },
            onError() {
              toastError(general.fr.message.operationFailed);
            },
          });
        }}
      />
    </section>
  );
};

export default EventStatistics;
